import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SetupModeSelectorProps {
  onSelectVoice: () => void;
  onSelectChat: () => void;
  onUploadImage: () => void;
  onSelectListener: () => void;
}

const GENIE_AVATAR = "/genie-mascots/role/support.png";

export const SetupModeSelector = ({ onSelectVoice, onSelectChat, onUploadImage, onSelectListener }: SetupModeSelectorProps) => {
  const navigate = useNavigate();

  return (
    <div className="h-screen w-full bg-[#F9FAFC] flex flex-col">
      {/* Top bar */}
      <div className="px-4 pt-3 md:px-6 md:pt-4">
        <Button
          onClick={() => window.history.back()}
          variant="ghost"
          size="sm"
          className="text-gray-600 px-2 md:px-3 py-1 md:py-2 text-sm md:text-base min-w-0 h-8 md:h-9 flex items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
        >
          <ChevronLeft size={16} />
          <span className="hidden md:inline">Exit</span>
        </Button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12 overflow-y-auto">
        {/* Genie avatar */}
        <img
          src={GENIE_AVATAR}
          alt="Help Genie"
          className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover mb-6 ring-2 ring-primary/10"
        />

        {/* Heading */}
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 text-center mb-2">
          How would you like to set up your Genie?
        </h1>
        <p className="text-sm md:text-base text-gray-500 text-center mb-8 md:mb-10 max-w-md">
          Pick a ready-made Genie from our catalogue or build one from scratch.
        </p>

        {/* Mode cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full max-w-4xl">
          {/* Browse Genies card */}
          <button
            onClick={() => navigate("/genie-hub/templates")}
            className="group relative rounded-xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02]"
          >
            <span className="absolute -top-3 right-4 z-10 px-3 py-1 rounded-full bg-gradient-to-r from-genie-primary to-genie-secondary text-white text-xs font-semibold tracking-wide shadow-md shadow-genie-primary/30">
              Recommended
            </span>
            <div className="relative bg-white rounded-[10.5px] p-6 md:p-8 flex flex-col items-center text-center h-full">
              <GenieIcon
                name="dashboard"
                size={56}
                className="w-14 h-14 md:w-16 md:h-16 mb-4"
              />
              <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1.5">
                Browse Genies
              </h3>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                Pick a ready-made Genie for the job. Support, sales, compliance, and more.
              </p>
            </div>
          </button>

          {/* Chat card */}
          <button
            onClick={onSelectChat}
            className="flex-1 group relative rounded-xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02]"
          >
            <span className="absolute -top-3 right-4 z-10 px-3 py-1 rounded-full bg-genie-primary text-white text-xs font-semibold tracking-wide shadow-md shadow-genie-primary/30">
              Fast
            </span>
            <div className="bg-white rounded-[10.5px] p-6 md:p-8 flex flex-col items-center text-center h-full">
              <GenieIcon
                name="chat"
                size={56}
                className="w-14 h-14 md:w-16 md:h-16 mb-4"
              />
              <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1.5">
                Type to Set Up
              </h3>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                Chat your way through the setup. Describe what you need and we'll build it.
              </p>
            </div>
          </button>

          {/* Voice card */}
          <button
            onClick={onSelectVoice}
            className="flex-1 group relative rounded-xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02]"
          >
            <span className="absolute -top-3 right-4 z-10 px-3 py-1 rounded-full bg-genie-secondary text-white text-xs font-semibold tracking-wide shadow-md shadow-genie-secondary/30">
              Magical
            </span>
            <div className="bg-white rounded-[10.5px] p-6 md:p-8 flex flex-col items-center text-center h-full">
              <GenieIcon
                name="microphone"
                size={56}
                className="w-14 h-14 md:w-16 md:h-16 mb-4"
              />
              <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1.5">
                Talk to Set Up
              </h3>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                Speak with our setup guide. We'll build your genie as you talk.
              </p>
            </div>
          </button>
        </div>

        {/* Listener Genie card */}
        <button
          onClick={onSelectListener}
          className="mt-6 w-full max-w-4xl group relative rounded-xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.01]"
        >
          <div className="bg-white rounded-[10.5px] p-5 md:p-6 flex items-center gap-5 text-left">
            <GenieIcon
              name="microphone"
              size={48}
              className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base md:text-lg font-semibold text-gray-800">Listener Genie</h3>
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">New</span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                Record meetings and video calls. Your Genie listens, transcribes with speaker labels, and delivers a full recap with action items to your Inbox.
              </p>
            </div>
          </div>
        </button>

        {/* Upload Image option */}
        <button
          onClick={onUploadImage}
          className="mt-6 md:mt-8 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <GenieIcon
            name="eye"
            size={20}
            className="w-5 h-5 opacity-60"
          />
          Upload Image Instead
        </button>
      </div>
    </div>
  );
};
